import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { DCard, BtnGold } from './MarketplaceAtoms.js';
import VinylCover from './VinylCover.js';
import {
  fetchSellerPremiumStats,
  purchaseSellerStats30d,
} from '../../lib/groovesSpendService.js';
import {
  GROOVES_LEDGER_REASON_LABELS,
  GROOVES_PHONE_GATE_USER_MESSAGE,
  LISTING_FEATURE_COST,
  SELLER_STATS_30D_COST,
  profileGroovesBalance,
} from '../constants/groovesEconomy.js';

function formatEuro(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return `${x.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

/** Consejos automáticos a partir del payload RPC v2. */
export function buildSellerStatsInsights(stats) {
  if (!stats?.ok) return [];
  const tips = [];
  const top = stats.top_listings_by_views?.[0];
  const zero = Number(stats.listings_zero_views_active) || 0;

  if (zero >= 2) {
    tips.push(`${zero} anuncios activos sin visitas — revisa portada, título y precio.`);
  }
  if (top && (top.wishlist_count ?? 0) >= 2 && (top.views ?? 0) >= 5) {
    tips.push(
      `«${top.title}» tiene interés (${top.wishlist_count} favoritos) — valora destacarlo ${LISTING_FEATURE_COST} G.`
    );
  }
  if ((stats.chats_started_30d ?? 0) >= 3 && (stats.sales_last_30d ?? 0) === 0) {
    tips.push('Tienes conversaciones recientes pero sin ventas cerradas — responde rápido y confirma envío.');
  }
  if ((stats.listing_views_30d ?? 0) >= 20 && (stats.sales_last_30d ?? 0) === 0) {
    tips.push('Muchas visitas y pocas ventas — prueba ajustar precio o condición en la descripción.');
  }
  if ((stats.featured_active_count ?? 0) === 0 && (stats.listings_active ?? 0) >= 2) {
    tips.push(`Ningún anuncio destacado ahora — el destacado prioriza tu disco en el mercado (${LISTING_FEATURE_COST} G / 7 d).`);
  }
  if ((stats.grooves_spent_30d ?? 0) > (stats.grooves_earned_30d ?? 0) * 1.5 && (stats.grooves_spent_30d ?? 0) > 50) {
    tips.push('Has gastado más Grooves de los que has ganado este mes — revisa escaneos IA y destacados.');
  }
  if (!tips.length && (stats.sales_last_30d ?? 0) > 0) {
    tips.push(`Buen mes: ${stats.sales_last_30d} venta(s) y ${formatEuro(stats.revenue_last_30d)} facturados.`);
  }
  return tips.slice(0, 3);
}

function StatTile({ value, label, accent }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '10px 6px',
        borderRadius: 10,
        background: 'rgba(255,255,255,0.04)',
        border: accent ? '0.5px solid rgba(200,168,75,0.25)' : '0.5px solid rgba(255,255,255,0.06)',
      }}
    >
      <div style={{ fontSize: 17, fontWeight: 700, color: accent ? C.gold : '#fff', lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 4, lineHeight: 1.3 }}>{label}</div>
    </div>
  );
}

function listingStubFromRow(row) {
  if (!row) return null;
  return {
    id: row.id,
    title: row.title,
    artist: row.artist,
    price: row.price,
    coverUrl: row.cover_url,
    photos: row.cover_url ? [row.cover_url] : [],
    status: row.status,
  };
}

export default function SellerPremiumStatsPanel({ profile, phoneVerified, onNavigate, onProfileRefresh }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchaseBusy, setPurchaseBusy] = useState(false);
  const [err, setErr] = useState('');

  const statsActive =
    profile?.seller_stats_until && new Date(profile.seller_stats_until).getTime() > Date.now();

  const loadStats = useCallback(async () => {
    if (!statsActive) {
      setStats(null);
      return;
    }
    setLoading(true);
    setErr('');
    const res = await fetchSellerPremiumStats();
    setLoading(false);
    if (!res.ok) {
      if (res.error !== 'stats_not_active') setErr(res.message || 'Error al cargar estadísticas.');
      setStats(null);
      return;
    }
    setStats(res);
  }, [statsActive]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const insights = useMemo(() => buildSellerStatsInsights(stats), [stats]);

  const handlePurchase = async () => {
    if (!phoneVerified) {
      setErr(GROOVES_PHONE_GATE_USER_MESSAGE);
      return;
    }
    if (profileGroovesBalance(profile) < SELLER_STATS_30D_COST) {
      setErr(`Necesitas ${SELLER_STATS_30D_COST} Grooves.`);
      return;
    }
    setPurchaseBusy(true);
    setErr('');
    const res = await purchaseSellerStats30d();
    setPurchaseBusy(false);
    if (!res.ok) {
      setErr(res.message || 'No se pudo activar.');
      return;
    }
    await onProfileRefresh?.();
    const fresh = await fetchSellerPremiumStats();
    if (fresh.ok) setStats(fresh);
  };

  if (!profile) return null;

  return (
    <DCard style={{ padding: 14, marginBottom: 14, borderColor: 'rgba(200,168,75,0.22)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Panel del vendedor</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.42)', marginTop: 4, lineHeight: 1.45 }}>
            Premium · últimos 30 días · visitas, ingresos, favoritos e insights.
          </div>
        </div>
        {statsActive ? (
          <span style={{ fontSize: 9, color: C.green, fontWeight: 600, whiteSpace: 'nowrap' }}>
            Hasta {new Date(profile.seller_stats_until).toLocaleDateString('es-ES')}
          </span>
        ) : null}
      </div>

      {err ? <div style={{ fontSize: 11, color: C.danger, marginBottom: 8 }}>{err}</div> : null}

      {!statsActive ? (
        <div>
          <p style={{ margin: '0 0 10px', fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.45 }}>
            Gratis: ventas y valoración en el resumen. Premium: ingresos, visitas 30 d, favoritos, chats, top anuncios con
            consejos y desglose Grooves.
          </p>
          <BtnGold
            type="button"
            disabled={purchaseBusy || !phoneVerified}
            onClick={handlePurchase}
            style={{ padding: '8px 14px', fontSize: 11, opacity: !phoneVerified ? 0.55 : 1 }}
          >
            {purchaseBusy ? '…' : `Activar 30 días · ${SELLER_STATS_30D_COST} Grooves`}
          </BtnGold>
          {!phoneVerified ? (
            <button
              type="button"
              onClick={() => onNavigate?.('ajustes', { initialSub: 'cuenta' })}
              style={{
                display: 'block',
                marginTop: 8,
                background: 'none',
                border: 'none',
                padding: 0,
                color: C.gold,
                fontSize: 10,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Verificar teléfono en Ajustes
            </button>
          ) : null}
        </div>
      ) : loading ? (
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Cargando panel…</div>
      ) : stats ? (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
            <StatTile value={stats.listing_views_30d ?? 0} label="Visitas 30 d" accent />
            <StatTile value={formatEuro(stats.revenue_last_30d)} label="Ingresos 30 d" accent />
            <StatTile value={stats.sales_last_30d ?? 0} label="Ventas 30 d" accent />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 12 }}>
            <StatTile value={stats.wishlist_total ?? 0} label="Favoritos" />
            <StatTile value={stats.chats_started_30d ?? 0} label="Chats 30 d" />
            <StatTile
              value={stats.rating_avg != null ? `${stats.rating_avg}★` : '—'}
              label={`Valoración (${stats.rating_count ?? 0})`}
            />
            <StatTile value={stats.grooves_earned_30d ?? 0} label="Grooves +" />
          </div>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px 14px',
              fontSize: 10,
              color: 'rgba(255,255,255,0.5)',
              marginBottom: 12,
              padding: '8px 10px',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.03)',
            }}
          >
            <span>Activos: {stats.listings_active ?? 0}</span>
            <span>Reservados: {stats.listings_reserved ?? 0}</span>
            <span>Vendidos: {stats.listings_sold ?? 0}</span>
            <span>Destacados: {stats.featured_active_count ?? 0}</span>
            <span>Visitas totales: {stats.total_listing_views ?? 0}</span>
            <span>Ingresos totales: {formatEuro(stats.revenue_total)}</span>
            {stats.avg_sale_30d != null ? <span>Ticket medio 30 d: {formatEuro(stats.avg_sale_30d)}</span> : null}
            <span>Grooves gastados 30 d: {stats.grooves_spent_30d ?? 0}</span>
          </div>

          {insights.length ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: C.gold, fontWeight: 700, marginBottom: 6, letterSpacing: '0.04em' }}>
                INSIGHTS
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10, color: 'rgba(255,255,255,0.72)', lineHeight: 1.55 }}>
                {insights.map((t, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {(stats.grooves_earned_by_reason ?? []).length ? (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 6 }}>Grooves ganados (desglose)</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(stats.grooves_earned_by_reason ?? []).map(row => (
                  <span
                    key={row.reason}
                    style={{
                      fontSize: 9,
                      padding: '4px 8px',
                      borderRadius: 999,
                      background: 'rgba(200,168,75,0.1)',
                      border: '0.5px solid rgba(200,168,75,0.2)',
                      color: 'rgba(255,255,255,0.75)',
                    }}
                  >
                    {GROOVES_LEDGER_REASON_LABELS[row.reason] || row.reason}: +{row.total}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {(stats.top_listings_by_views ?? []).length ? (
            <div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>Top anuncios</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(stats.top_listings_by_views ?? []).map(row => {
                  const stub = listingStubFromRow(row);
                  const featured =
                    row.featured_until && new Date(row.featured_until).getTime() > Date.now();
                  return (
                    <div
                      key={row.id}
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: 10,
                        borderRadius: 10,
                        background: 'rgba(255,255,255,0.04)',
                        border: featured ? '0.5px solid rgba(200,168,75,0.35)' : '0.5px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <div style={{ width: 48, height: 48, borderRadius: 8, flexShrink: 0, overflow: 'hidden' }}>
                        <VinylCover item={stub} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: '#fff',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.title || 'Sin título'}
                        </div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                          {row.artist || '—'} · {formatEuro(row.price)}
                        </div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          <span>{row.views ?? 0} visitas</span>
                          <span>{row.views_30d ?? 0} (30 d)</span>
                          <span>{row.wishlist_count ?? 0} fav.</span>
                          {featured ? <span style={{ color: C.gold }}>Destacado</span> : null}
                        </div>
                      </div>
                      {onNavigate && stub ? (
                        <button
                          type="button"
                          onClick={() => onNavigate('ficha', stub)}
                          style={{
                            alignSelf: 'center',
                            flexShrink: 0,
                            padding: '6px 10px',
                            borderRadius: 8,
                            border: `0.5px solid ${C.gold}`,
                            background: 'transparent',
                            color: C.gold,
                            fontSize: 9,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          Ver
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </DCard>
  );
}
